import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { SharedModule } from '@app/shared/shared.module';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ImageUrls } from '@app/shared/constants/image-urls-const';
import { ReportService } from '../services/report.service';
import { FileDropDirective } from '../directives/file-drop.directive';
import { Utilities } from '@app/core/utils/utilities';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LoaderService } from '@app/core/services/loader.service';
import { ReportConstants } from '../util/report-constants';
import * as ExcelJS from 'exceljs';

@Component({
  selector: 'app-upload-report',
  imports: [CommonModule, SharedModule, MatProgressBarModule, FileDropDirective],
  templateUrl: './upload-report.component.html',
  styleUrl: './upload-report.component.css',
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UploadReportComponent {
  matData = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<UploadReportComponent>);
  imageConst = inject(ImageUrls);
  reportService = inject(ReportService);
  util = inject(Utilities);
  datePipe = inject(DatePipe);
  loaderService = inject(LoaderService);
  reportConstants: any = inject(ReportConstants);

  uploadProgress = signal<number>(0);
  fileSize = signal<string>('');
  fileName = signal<string>('');
  selectedFile = signal<any>(null);
  uploadedFile = signal<any>(null);
  reportTitle = signal<string>('');
  warningMessage = signal<string>('');
  progressType = signal<string>('');
  instituitonIdCheck = signal<boolean>(false);

  maxSizeInMB: number = 5;
  acceptedFileFormatList: string[] = ['.xlsx', '.xls'];
  acceptedFileFormat: string = this.acceptedFileFormatList?.join(', ');
  fileFormatPattern = new RegExp(`\\.(${this.acceptedFileFormatList.join('|').replaceAll('.', '')})$`, 'i');
  reportPageName: string = this.matData?.fromPage?.toLowerCase() || 'subject';
  reportPageData: any = this.reportConstants?.[this.reportPageName];
  headerDetails: string[] = [];
  availableHeaders: any = [];

  isSubmitDisabled = computed(() => !this.uploadedFile() || !this.reportTitle().trim());

  uploadFile(fileEvent: any) {
    this.headerDetails = this.getEntityBasedHeaderData('headerData');
    this.availableHeaders = this.getEntityBasedHeaderData('availableHeaderData');
    this.clearFileData();
    if (fileEvent?.files?.length)
      if (!this.fileFormatPattern.test(fileEvent?.files[0]?.name)) {
        this.warningMessage.set('Invalid file format');
      } else {
        this.selectedFile.set(fileEvent);
        const file: File = fileEvent.files[0];
        let totalBytes = file.size;
        if (totalBytes > (this.maxSizeInMB * 1024 * 1024)) {
          this.warningMessage.set('File size exceeds maximum limit');
        } else {
          this.readFile(file);
          const sizeExt = ['Bytes', 'KB', 'MB', 'GB'];
          let idx = 0;
          while (totalBytes > 900) {
            totalBytes /= 1024;
            idx++;
          }
          this.fileSize.set((Math.round(totalBytes * 100) / 100) + ' ' + sizeExt[idx]);
          this.fileName.set(file.name);
        }
      }
  }

  readFile(file: File) {
    const reader = new FileReader();
    reader.onload = async (fileData) => {
      this.selectedFile.set(null);
      this.uploadedFile.set(file);
      this.progressType.set('loaded');

      // if (this.matData?.institutionId) {
      const arrayBuffer = fileData?.target?.result as ArrayBuffer;
      const workbook = new ExcelJS.Workbook();

      await workbook.xlsx.load(arrayBuffer);
      const worksheet = workbook.worksheets[0];
      const wsData: any[] = [];

      worksheet.eachRow((row) => {
        const rowData: any = [];
        row.eachCell((cell) => {
          rowData.push(cell.value);
        });
        if (rowData.length)
          wsData.push(rowData);
      });

      if (wsData.length) {
        let institution: any = '';
        wsData.forEach((data: any, index: number) => {
          if (data.includes('Institution ID') && this.reportPageName != 'subject')
            institution = wsData[index + 1][data.indexOf('Institution ID')];
          if (['institution', 'subject', 'stagesmodules', 'clearing'].includes(this.reportPageName))
            this.checkHeaderValidation(data);
        });
        if (this.matData?.institutionId && this.reportPageName !== 'clearing')
          this.instituitonIdCheck.set(institution != this.matData?.institutionId);
      }
      // }
    };

    reader.onerror = () => {
      console.error('Error reading file');
      this.progressType.set('error');
    };
    reader.readAsArrayBuffer(file);
  }

  checkHeaderValidation(data: any) {
    if (this.reportPageName === 'institution') {
      const entityName = this.util.dataTypeOf(data[0]) === 'string' ? data[0]?.replace(' ID', '') : '';
      if (this.headerDetails.includes(entityName)) {
        const entityDetails = this.reportConstants[entityName.toLowerCase() + 'Headers'];
        this.availableHeaders[entityName] = true;
        const returnVal = this.compareArray(data, entityDetails?.headerDetails)
        if (returnVal) {
          const index = this.headerDetails.indexOf(entityName);
          this.headerDetails.splice(index, 1);
        }
      }
    } else if (this.reportPageName === 'clearing') {
      const entityDetails = this.reportConstants['clearingHeaders'];
      const requiredHeaders: string[] = entityDetails?.headerDetails || [];
      const dataLower = data?.map((item: any) => (typeof item === 'string' ? item.toLowerCase().trim() : ''));
      const allPresent = requiredHeaders.every((header: string) => dataLower.includes(header.toLowerCase().trim()));
      if (allPresent) {
        this.availableHeaders['Clearing'] = true;
        const index = this.headerDetails.indexOf('Clearing');
        if (index > -1) 
          this.headerDetails.splice(index, 1);
      }
    } else {
      const entityHeaderName = (this.util.dataTypeOf(data[0]) === 'string' && data[0] === 'Course Instance ID *') ? 'Stages' : (this.matData?.fromPage?.toLowerCase() === 'stagesmodules' ? '' : this.reportPageName);
      if (Array.isArray(this.headerDetails) && this.headerDetails.includes(entityHeaderName)) {
        const entityDetails = this.reportConstants[entityHeaderName.toLowerCase() + 'Headers'];
        this.availableHeaders[entityHeaderName] = true;
        const returnVal = this.compareArray(data, entityDetails?.headerDetails)
        if (returnVal) {
          const index = this.headerDetails.indexOf(entityHeaderName);
          this.headerDetails.splice(index, 1);
        }
      }
    }
  }

  clearFileData() {
    this.selectedFile.set(null);
    this.uploadedFile.set(null);
    this.uploadProgress.set(0);
    this.fileSize.set('');
    this.fileName.set('');
    this.warningMessage.set('');
    this.progressType.set('');
    this.instituitonIdCheck.set(false);
    this.headerDetails = this.getEntityBasedHeaderData('headerData');
    this.availableHeaders = this.getEntityBasedHeaderData('availableHeaderData');
  }

  uploadReportFile(response: any) {
    const preSignedUrl = response?.preSignedUrl;
    if (preSignedUrl)
      this.reportService.uploadReportFile(preSignedUrl, this.uploadedFile()).subscribe({
        next: () => {
          this.dialogRef.close(this.reportPageData?.successMsg);
          this.loaderService.forceCloseOverlay();
        },
        error: (error) => console.error('Error uploading file', error)
      });
    else
      this.loaderService.forceCloseOverlay();
  }

  uploadSubjectReport() {
    const fileName = this.fileName()?.replace(this.fileFormatPattern, '');
    const currentDate = this.datePipe.transform(new Date(), 'yyyyMMdd_HHmmss');
    const fileData: any = {
      reportTitle: this.reportTitle()?.trim() || '',
      fileName: this.reportPageData?.uploadFilePath + fileName + '_' + currentDate,
      fileType: '.' + this.fileName().split('.').pop()
    }
    if (this.util.getStoredUserDetails().userId)
      fileData['uploadedBy'] = Number(this.util.getStoredUserDetails().userId);
    if (this.matData?.institutionId) {
      this.getEasyLoadPreSignedUrl({ ...fileData, entityType: this.reportPageData?.entityType });
    } else {
      let mismatchedHeaders: any = [];
      if (this.reportPageName === 'subject')
        mismatchedHeaders = this.headerDetails;
      if (mismatchedHeaders?.length)
        this.warningMessage.set('Incorrect report template. Please upload the correct report.');
      else
        this.getUploadPreSignUrl(fileData);
    }
  }

  getUploadPreSignUrl(fileData: any) {
    this.reportService?.getUploadPreSignedUrl(fileData)?.subscribe({
      next: (res: any) => {
        this.uploadReportFile(res);
      },
      error: (error) => console.error('Error getting upload presigned url', error)
    })
  }

  getEasyLoadPreSignedUrl(fileData: any) {
    let mismatchedHeaders: string[] = [];
    if (this.matData?.fromPage?.toLowerCase() === 'institution') {
      const availableEntities = Object.keys(this.availableHeaders)?.filter((entityName: string) => this.availableHeaders[entityName]);
      mismatchedHeaders = availableEntities?.filter((entityName: any) => this.headerDetails.includes(entityName))
    } else if (this.matData?.fromPage?.toLowerCase() === 'stagesmodules' || this.matData?.fromPage?.toLowerCase() === 'clearing') {
      mismatchedHeaders = this.headerDetails;
    }
    if (this.instituitonIdCheck())
      this.warningMessage.set('Upload Failed! Institution ID mismatched.');
    else if (mismatchedHeaders?.length)
      if (this.matData?.fromPage?.toLowerCase() === 'institution')
        this.warningMessage.set(mismatchedHeaders.join(', ') + ' headers mismatch');
      else
        this.warningMessage.set('The uploded template format is incorrect. Please upload the correct template.');
    else
      this.reportService?.getEasyLoadPreSignedUrl(fileData, this.matData?.institutionId)?.subscribe({
        next: (res: any) => {
          this.uploadReportFile(res);
        },
        error: (error) => console.error('Error getting upload presigned url', error)
      })
  }

  compareArray(arr1: string[], arr2: string[]): boolean {
    if (arr1?.length !== arr2?.length) return false;
    return arr1?.every((val, index) => val === arr2[index]);
  }

  getEntityBasedHeaderData(type: string) {
    if (this.matData?.fromPage?.toLowerCase() === 'institution')
      return type === 'headerData' ? structuredClone(this.reportConstants.headerDetails) : structuredClone(this.reportConstants.entityHeaderAvailability);
    else if (this.matData?.fromPage?.toLowerCase() === 'stagesmodules')
      return type === 'headerData' ? structuredClone(this.reportConstants.stagesModulesHeaderDetails) : structuredClone(this.reportConstants.stgModEntityHeaderAvailability);
    else if (this.matData?.fromPage?.toLowerCase() === 'clearing')
      return type === 'headerData' ? structuredClone(this.reportConstants.clearingHeaderDetails) : structuredClone(this.reportConstants.clearingHeaderAvailability);
    else
      return type === 'headerData' ? structuredClone(this.reportConstants[this.reportPageName + 'HeaderDetails']) : structuredClone(this.reportConstants[this.reportPageName + 'HeaderAvailability']);
  }
}
